import { IdGenerator } from '@src/editor/services/IdGenerator';

import type { ChapterBoundary } from './chapterizationResponse';
import type { NameSource, Reel } from './reelTypes';

/**
 * PLAT-11099 — the validated boundaries, written as the reel's chapters (E8).
 *
 * A whole-reel run **replaces**: the returned set is the only set, because the
 * creator was told so before it ran (DS7). Section names are not chapters and
 * survive untouched, and so does `disabledSections` — every writer in this
 * module spreads `...reel` first for that reason, and
 * `reelWritersKeepDisabled.test.ts` locks the invariant in.
 *
 * Titles land as `'generated'` (DS12) so that a later per-chapter re-generation
 * from the heading's menu treats them as regenerable, while a title the creator
 * types afterwards still is not (DE10). A boundary that arrived with no usable
 * title is `'clip'`, which renders as its ordinal (PLAT-11055 DS5) — the same
 * provenance a hand-inserted chapter starts with, because that is exactly what
 * it is: a boundary nobody has named.
 *
 * Pure and non-mutating; the caller writes the result into `clipJson.reel`,
 * which is handed around by reference.
 */
export function applyChapterization(
  reel: Reel | undefined,
  boundaries: readonly ChapterBoundary[],
  /**
   * PLAT-11154 S6 — titles this run carried over rather than wrote, keyed by the
   * Section their chapter opens at, with the provenance they had. A subset run
   * edits chapters in place, so DE10 applies literally: recording a title the
   * creator typed as `'generated'` would quietly make it replaceable by the next
   * generation that came along.
   */
  preservedTitleSources?: ReadonlyMap<string, NameSource>,
): Reel {
  return {
    ...reel,
    chapters: boundaries.map(boundary => ({
      id: IdGenerator.shortRandomId(),
      title: boundary.title ?? '',
      titleSource: boundary.title
        ? (preservedTitleSources?.get(boundary.firstSectionId) ?? 'generated')
        : ('clip' as const),
      firstSectionId: boundary.firstSectionId,
    })),
    sections: { ...reel?.sections },
  };
}
